import { Link } from "react-router-dom";
import { Star, ArrowRight, Sparkles, Palette, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import QuoteForm from "@/components/QuoteForm";
import SEO from "@/components/SEO";

import hero1 from "@/assets/hero-1.jpg";
import hero2 from "@/assets/hero-2.jpg";
import gallery1 from "@/assets/gallery-1.jpg";
import gallery2 from "@/assets/gallery-2.jpg";
import gallery3 from "@/assets/gallery-3.jpg";
import gallery4 from "@/assets/gallery-4.jpg";
import gallery5 from "@/assets/gallery-5.jpg";
import gallery6 from "@/assets/gallery-6.jpg";

const featuredWork = [
  { src: gallery1, title: "Wildflower Bottle" },
  { src: gallery2, title: "Wedding Portrait" },
  { src: gallery3, title: "Landscape Bottle" },
  { src: gallery4, title: "Pet Portrait" },
  { src: gallery5, title: "Anniversary Bottle" },
  { src: gallery6, title: "Family Portrait" },
];

const reviews = [
  { name: "Emma S.", text: "Absolutely beautiful work! The bottle art was even better than I imagined. Perfect anniversary gift.", rating: 5 },
  { name: "James R.", text: "The custom painting of our dog was incredible. So much detail and care went into it. Highly recommend!", rating: 5 },
  { name: "Sophie L.", text: "Ordered a wedding bottle for my best friend — she cried happy tears! Fast delivery too.", rating: 5 },
];

const Index = () => {
  return (
    <Layout>
      <SEO
        title="Soft Chaos Studio · Personalised Handmade Gifts & Custom Art"
        description="Personalised handmade gifts, custom portraits, painted bottles & bespoke art. Unique gifts made with love in the UK."
        canonical="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Soft Chaos Studio",
          "url": "https://soft-chaos-studio-web.lovable.app",
          "telephone": "+447414896899",
          "email": "softchaos.uk@gmail.com",
          "description": "Personalised handmade gifts, custom portraits, painted bottles & bespoke art.",
        }}
      />
      {/* Hero */}
      <section className="bg-blush">
        <div className="container py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold leading-tight mb-5">
                Personalised handmade gifts designed for you
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                Bottle art, paintings and one-of-a-kind pieces designed around your story.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/quote">
                  <Button size="lg">Request a Quote</Button>
                </Link>
                <Link to="/gallery">
                  <Button variant="outline" size="lg">View Gallery</Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <img src={hero1} alt="Handmade bottle art gifts with soft pastel tones" className="rounded-lg shadow-card w-full aspect-square object-cover" loading="lazy" />
              <img src={hero2} alt="Custom hand-painted portrait in an artist studio" className="rounded-lg shadow-card w-full aspect-square object-cover mt-8" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container py-20">
        <h2 className="text-3xl font-serif font-semibold text-center mb-12">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Sparkles, title: "Share your idea", desc: "Tell us about the person and occasion — we'll listen to every detail." },
            { icon: Palette, title: "We design it with you", desc: "We create concepts and work with you until it's exactly right." },
            { icon: Truck, title: "We create & deliver", desc: "Your handmade gift is carefully crafted and safely delivered to your door." },
          ].map((step, i) => (
            <div key={i} className="text-center p-8 rounded-lg bg-card shadow-card">
              <div className="w-14 h-14 rounded-full bg-sage mx-auto flex items-center justify-center mb-4">
                <step.icon size={24} className="text-secondary-foreground" />
              </div>
              <h3 className="font-serif text-lg font-semibold mb-2">{step.title}</h3>
              <p className="text-sm text-muted-foreground">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Work */}
      <section className="bg-sage">
        <div className="container py-20">
          <h2 className="text-3xl font-serif font-semibold text-center mb-12">Featured Work</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {featuredWork.map((item, i) => (
              <Link to="/gallery" key={i} className="group">
                <div className="rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-shadow duration-300">
                  <img
                    src={item.src}
                    alt={item.title}
                    className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <p className="text-sm font-medium mt-2 text-center">{item.title}</p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/gallery">
              <Button variant="outline">
                View Full Gallery <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Preview */}
      <section className="container py-20">
        <h2 className="text-3xl font-serif font-semibold text-center mb-12">What Our Customers Say</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((review, i) => (
            <div key={i} className="bg-card rounded-lg p-6 shadow-card">
              <div className="flex gap-1 mb-3">
                {Array.from({ length: review.rating }).map((_, j) => (
                  <Star key={j} size={16} className="fill-primary text-primary" />
                ))}
              </div>
              <p className="text-sm text-muted-foreground mb-4 italic">"{review.text}"</p>
              <p className="font-medium text-sm">{review.name}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/reviews">
            <Button variant="outline">View All Reviews</Button>
          </Link>
        </div>
      </section>

      {/* Quote Section */}
      <section id="quote" className="bg-blush">
        <div className="container py-20">
          <h2 className="text-3xl font-serif font-semibold text-center mb-3">Request a Quote</h2>
          <p className="text-center text-muted-foreground mb-10">Tell us about your idea and we'll bring it to life.</p>
          <QuoteForm />
        </div>
      </section>
    </Layout>
  );
};

export default Index;
